import axios, {AxiosResponse} from "axios";
import {createClient} from "@connectrpc/connect";
import {TRANSFER_UPLOADS_PATH} from "api/endpointPaths.mjs";
import {apiTransport} from "../../api/connectTransport";
import type {UploadInitResponse} from "api/transferApi";
import type {AssertNoSpecDrift, RequestSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";
import ExpiresAfter, {expiresAfterFromName, ExpiresAfterName} from "../../domain/ExpiresAfter";
import {TransferFileAdditionState} from "../../domain/TransferFileAdditionState";
import {TransferLegalHoldState} from "../../domain/TransferLegalHoldState";
import {TransferRecoveryState} from "../../domain/TransferRecoveryState";
import {TransferChangeService} from "../../gen/kitecorp/transferify/transfer/v1/transfer_changes_pb";
import {
	ListTransfersRequest_Sort,
	TransferListService,
} from "../../gen/kitecorp/transferify/transfer/v1/transfer_list_pb";
import type {TransferState as TransferStateMessage} from "../../gen/kitecorp/transferify/transfer/v1/transfer_pb";
import {responseStatus} from "../../utils/ErrorUtils";
import type {SenderCompany} from "../../sender/senderChoice";
import {
	transferFileOf, transferListPageOf, transferStateOf, updateTransferRequestOf,
} from "../transferMessages";

export type UpdateRequest = {
	oldEmail: string,
	email: string,
	fullname: string,
	description: string
}
export type CreateRequest = {
	email: string,
	fullname: string,
	description: string
}

export interface TransferFile {
	id: string,
	name: string,
	/**
	 * The size of the file the sender picked, in bytes — NOT the stored size the api serves, which is the
	 * ciphertext for an encrypted file. `transferFileOf` (`transfers/transferMessages`) converts at the boundary, so
	 * every row, total and size sort reads one honest value.
	 */
	length: number,
	/**
	 * Whether this file's bytes are stored encrypted. Its OWN flag, not its transfer's: a transfer created
	 * unencrypted takes encrypted additions (kitecorp/transferify-ui#2997), so one transfer can hold both.
	 * The api serves it on every file; absent only on fixtures that predate it.
	 */
	encrypted?: boolean,
	/** Null when the file's hash is not recorded: the contract leaves it out then. */
	hash: string | null,
	/** Null when the file has no peer-to-peer identity recorded: the contract leaves it out then. */
	magnetURI: string | null,
	createdDate: string,
	/**
	 * The file's folder path including its name, or absent/null for a root-level file. Untrusted on arrival:
	 * re-sanitized before display or archive use (`core/transfer/relativePath`).
	 */
	relativePath?: string | null,
}

/**
 * One sender-owned transfer's state, the app-facing shape of the api `TransferStateDto`, which the
 * list row ({@link TransferListEntry}) extends and the post-send changes answer. Its fields
 * are PER-TRANSFER. {@link expireAfter} is mapped from
 * the contract's enum to the numeric {@link ExpiresAfter} at the boundary (`transferStateOf` in
 * `transfers/transferMessages`), so every
 * consumer stays in the one numeric enum the create flow and `home/gating` already use; it is null for
 * a transfer with no time-based TTL (instant/P2P NEVER). {@link recipients} is the canonical sorted
 * email list (empty for a link-only transfer), the working set for the edit dialog (#94).
 */
export interface TransferState {
	id: string,
	/**
	 * Encoded share id used by the active P2P seeder. Additive/optional so this UI remains compatible
	 * with the pre-revocation API, whose owner projection does not serialize it yet.
	 */
	encodedId?: string,
	message: string | null,
	keepOnline: boolean,
	expireAfter: ExpiresAfter | null,
	/** Sender opt-in for per-download email alerts on this transfer (api#92). */
	notifyOnDownload: boolean,
	createdDate: string,
	recipients: string[],
	/**
	 * When the download link stops working, as served by the api (#96) — NOT recomputed from
	 * `createdDate + expireAfter`, because a RECOVERED transfer's link outlives that arithmetic by its
	 * recovery window. Null for a transfer with no TTL.
	 */
	expiresAt: string | null,
	/** The api's recovery verdict for this transfer at request time (#96). */
	recoveryState: TransferRecoveryState,
	/** The deadline for recovering it — equally, when the retained bytes are purged. Null when none. */
	recoverableUntil: string | null,
	/**
	 * Whether this transfer may take added files, and if not, why — the api's own verdict
	 * (kitecorp/transferify-api-service#220), served on every transfer state and REQUIRED, mirroring the
	 * {@link recoveryState} precedent.
	 *
	 * One server-derived field rather than the raw shape facts (P2P / encryption flow / completeness /
	 * expiry) on purpose: this is the value `POST /transfer/{id}/files` itself switches on to refuse, so
	 * the affordance and the verdict cannot disagree. Handing the ui those raw facts instead would make
	 * it re-derive a grammar the server owns, and the re-derivation is what drifts into an "Add files"
	 * button that 400s.
	 *
	 * It answers SHAPE only — the `editAfterSending` entitlement stays a plan-catalogue read on this
	 * side, exactly as for the message/recipient edits.
	 */
	fileAdditionState: TransferFileAdditionState,
	/**
	 * Whether the transfer has been SENT: it has files and none is still mid-upload or aborted — every
	 * file is either stored or registered for peer delivery. Served on every transfer state and REQUIRED
	 * (kitecorp/transferify-api-service#220), mapped from the entity's `allFilesDeliverable()` — the same
	 * predicate the api's own removal gate splits on, so this side never re-derives the rule.
	 *
	 * REGISTERED FOR DELIVERY, not "every S3 object exists" (kitecorp/transferify-api-service#381). An
	 * instant peer-to-peer transfer streams over the data channel and never reaches storage, so the old
	 * escrow-shaped answer left it forever `false` and this field's REMOVE gate silently exempted every
	 * instant transfer from `editAfterSending`. It says nothing about LIVENESS: for an instant transfer it
	 * stays true after the sender goes offline, because the server tracks no signalling state here.
	 *
	 * A field of its own rather than something read off {@link fileAdditionState}, because that enum
	 * answers a different question and collapses this one: a PEER-TO-PEER transfer reports UNSUPPORTED
	 * whether or not it is live, so `state !== "INCOMPLETE"` would mis-read it. Consumed by the REMOVE gate.
	 */
	complete: boolean,
	/**
	 * Whether abuse reports have suspended this transfer
	 * (kitecorp/transferify-api-service#417): downloads are off and support is reviewing it.
	 *
	 * A BARE BOOLEAN, and the api serves nothing more on purpose — no category, no reporter, no count.
	 * The owner of a suspended transfer is the SUBJECT of the report, and a sender who could count the
	 * reporters could work out which recipient spoke up. If a future payload grows detail here, that is
	 * a product decision, not a mapping convenience.
	 */
	suspended: boolean,
	/**
	 * Whether the OWNER has placed this transfer under legal hold (kitecorp/transferify#85): the link
	 * stops expiring and the transfer cannot be deleted until they release it.
	 *
	 * Read TOGETHER with {@link expiresAt}, which the api serves as null while a hold is in place. The ui
	 * must never re-derive "held transfers do not expire" from `createdDate + expireAfter` — that is the
	 * arithmetic the hold suspends, and `expiresAtOf` consults this flag precisely to refuse it.
	 */
	legalHold: boolean,
	/**
	 * Whether this transfer could be held right now — the api's own five-way verdict
	 * (kitecorp/transferify#85), and the ONLY thing the "place hold" affordance may key on.
	 *
	 * The ui cannot compute it: one input is whether an already-written object's purge date can be moved,
	 * which depends on the storage provider recorded on this row. Both live environments write to a
	 * prefix-driven backend where it cannot be, so a client-side mirror of the shape guards alone would
	 * offer the action on every finite-retention transfer and be refused every time.
	 */
	legalHoldState: TransferLegalHoldState,
	/**
	 * How many recipient downloads of this transfer the OWNER's monthly window refused so far this UTC
	 * month (kitecorp/transferify-api-service#1192), served on every transfer state and REQUIRED. Zero
	 * when none. Counts refused ATTEMPTS: downloads are anonymous, so an attempt stands for a recipient.
	 *
	 * A FACT, not a verdict: the api does not say whether the window is still full, because that is the
	 * usage meter's answer (`GetDownloadUsage`) and the two must not be re-derived here. The
	 * row joins them in `transfers/refusalNotice.ts`.
	 */
	refusedDownloads: number,
	/**
	 * The company the transfer was sent as (kitecorp/transferify-ui#3186), or null for one a person sent. The owner's
	 * own record, served as it was sent; the row names it beside the other facts.
	 */
	senderCompany: SenderCompany | null,
}

/**
 * One row of the owner's paged transfer list, the api's `TransferSummaryDto`
 * (kitecorp/transferify-api-service#2163): the transfer's state with its files COUNTED and SIZED rather than
 * listed. The files are read when the row is expanded ({@link TransferApi.getFiles}).
 */
export interface TransferListEntry extends TransferState {
	/** How many files the transfer holds, whatever their upload status. */
	fileCount: number,
	/** The transfer's size as the sender picked its files: the api takes an encrypted file's framing off. */
	totalBytes: number,
	/** The first file's name in the transfer's own order, which titles the row; null when there is none. */
	firstFileName: string | null,
	/**
	 * The names of the contact groups the transfer was sent to (kitecorp/transferify-ui#3405), which the Sent
	 * tab's search reads beside the recipient addresses. Optional because a row cached by an older build, or
	 * served by an api that predates the field, carries none; {@link entryFromWire} answers `[]` for it.
	 */
	recipientGroups?: readonly string[],
	/**
	 * Whether the transfer is Zero-Knowledge: its key lives only in the link that was shared, which the Sent row says
	 * with its chip (kitecorp/transferify-ui#3405). The api's `Transfer.isZeroKnowledge()` on its list row
	 * (kitecorp/transferify-api-service#2597), optional in the spec and missing from a row an older build cached;
	 * absent reads as escrow, so no row claims a Zero-Knowledge transfer the api did not say it is.
	 */
	zeroKnowledge?: boolean,
}

/**
 * The orders the list can be asked for, sorted by the api across EVERY transfer of the account. They are
 * the four combinations of the list header's two toggles (`transfers/transferList.ts` maps them).
 */
export type TransferListSort = "NEWEST" | "OLDEST" | "LARGEST" | "NEWEST_THEN_SMALLEST";

/**
 * One page of the owner's transfer list: at most the api's page size of rows, and totals over EVERY transfer
 * of the account, which the list header reports whatever has been loaded.
 */
export interface TransferListPage {
	transfers: TransferListEntry[],
	totalTransfers: number,
	totalFiles: number,
	totalBytes: number,
}

/**
 * Body of `UpdateTransfer` (#93 + #94). Every field is optional; an ABSENT field means "leave
 * unchanged". {@link expireAfter} extends the retention (#93, `extendExpiry` capability); the other
 * three edit the sent transfer (#94, `editAfterSending`): {@link message} replaces the sender message
 * (blank clears it), {@link addRecipients} emails new mailboxes the link, {@link revokeRecipients}
 * stops listing/redelivering to them.
 */
export interface PatchTransferBody {
	expireAfter?: ExpiresAfter,
	message?: string,
	addRecipients?: string[],
	revokeRecipients?: string[],
}

/*
 * File ADD on an already-sent transfer (kitecorp/transferify-api-service#220).
 *
 * Every added file is uploaded ENCRYPTED, whatever the transfer it joins (owner decision 2026-09-27,
 * kitecorp/transferify-ui#2997): the request says `encrypted: true`, which the api accepts on a transfer
 * created unencrypted too, and answers each file's id for the batch multipart routes to initiate. A
 * transfer created unencrypted then holds both kinds of files, each downloaded by its own flag.
 */

/** One added file's declared metadata. Explicit `null`s: the api models both fields as nullable. */
export interface AddFileMeta {
	name: string,
	size: number,
	contentType: string | null,
	/** Folder-relative path INCLUDING the leaf name, already sanitized; null for a flat file. */
	relativePath: string | null,
}

export interface AddFilesRequest {
	files: AddFileMeta[],
	/**
	 * Always sent, and always `true`: without it the api keeps the transfer's own mode, which for a transfer
	 * created unencrypted would record the added files as unencrypted while the client uploads ciphertext.
	 */
	encrypted: true,
}

/** The addition's answer: the upload init's own shape, every file to be uploaded through the batch routes. */
export type AddFilesResponse = UploadInitResponse;

export type _AddFileMeta = AssertNoSpecDrift<RequestSpecDrift<AddFileMeta, Schemas["FileMeta"]>>;
export type _AddFilesRequest = AssertNoSpecDrift<RequestSpecDrift<AddFilesRequest, Schemas["AddTransferFilesRequest"]>>;
// AddFilesResponse is bound where UploadInitResponse is (`api/transferApi.ts`): it is that type.

/** Which recipient channel a delivery row covers. */
export type RecipientDeliverySource = "DIRECT" | "GROUP";

/** A channel's durable delivery state. */
export type RecipientDeliveryStatus =
	"PENDING" | "IN_PROGRESS" | "ACCEPTED" | "RETRYABLE" | "EXHAUSTED" | "SKIPPED";

/** The most recent provider verdict for a channel. */
export type RecipientDeliveryOutcome =
	"ACCEPTED" | "REJECTED" | "PARTIAL" | "AMBIGUOUS" | "UNKNOWN" | "SKIPPED";

/**
 * One notification channel's delivery outcome. Per CHANNEL, not per recipient — that is the granularity
 * the api stores, and it carries no email address by design.
 */
export interface TransferRecipientDelivery {
	source: RecipientDeliverySource,
	status: RecipientDeliveryStatus,
	/** Null while no attempt has completed. */
	lastOutcome: RecipientDeliveryOutcome | null,
	attemptCount: number,
	/** ISO-8601 local date-time, or null while the channel has not settled. */
	completedAt: string | null,
}

/**
 * Advanced analytics for one owned transfer — `GET /transfer/{id}/analytics`, PREMIUM only.
 *
 * Bounded by what the api actually records: it holds download COUNTERS, never per-download event rows,
 * so there is no time series, no per-recipient breakdown and no per-file count to consume. {@link downloads}
 * counts download-PAGE opens (a presigned object fetch is invisible to the backend, a P2P reconnect is
 * exempt, and a multi-file ZIP of one transfer is one download).
 */
export interface TransferAnalytics {
	transferId: string,
	downloads: number,
	/** The owner's cap, or null when the transfer is uncapped. */
	downloadLimit: number | null,
	/** Capacity left under that cap, or null when uncapped. */
	remainingDownloads: number | null,
	limitReached: boolean,
	/**
	 * Whether this transfer records durable per-channel delivery state at all. When false an empty
	 * {@link deliveries} means "never tracked", NOT "nothing was delivered" — the row predates the
	 * feature. Consumers must not collapse the two.
	 */
	deliveryTracked: boolean,
	deliveries: TransferRecipientDelivery[],
}

/**
 * What one entry of the download audit trail claims — and, crucially, ON WHOSE WORD.
 *
 * `PAGE_OPEN` and `ZIP_DOWNLOAD` are WITNESSED: the api saw them, inside the transaction that counted
 * them. `FILE_DOWNLOAD_STARTED` and `FILE_DOWNLOAD_COMPLETED` were REPORTED by the recipient's own
 * browser (kitecorp/transferify-api-service#881) — the only evidence a presigned per-file fetch or a
 * peer-to-peer receive can produce, since neither ever reaches the api — and they are strictly weaker.
 * The distinction is not cosmetic and `trailEntryClaimOf` is where the view is made to honour it.
 *
 * This union is what THIS build knows. The api ships independently, so a fifth kind can exist on the
 * wire before any name for it exists here; every consumer must degrade rather than assume.
 */
export type DownloadEventKind =
	"PAGE_OPEN" | "ZIP_DOWNLOAD" | "FILE_DOWNLOAD_STARTED" | "FILE_DOWNLOAD_COMPLETED";

/**
 * One recorded download event of a transfer (kitecorp/transferify#84, extended by
 * kitecorp/transferify-api-service#881).
 *
 * The nullable fields mean NOT OBSERVED, and that distinction must survive to the view: a download
 * whose origin the api could not resolve is not a download from an unknown place called "unknown". Nothing
 * here identifies a person — downloads are anonymous, so this is a network origin and nothing more.
 */
export interface TransferDownloadEvent {
	kind: DownloadEventKind,
	/** ISO-8601 local date-time. */
	occurredAt: string,
	/** Container-resolved client address, or null when it could not be resolved. */
	sourceIp: string | null,
	/**
	 * ISO-3166-1 alpha-2 from the edge, or null when no edge reported one. WEAKER evidence than
	 * {@link sourceIp}: a caller reaching the origin directly can forge it, so it is shown as context
	 * beside the address and never on its own authority.
	 */
	country: string | null,
	userAgent: string | null,
	/** How many files a ZIP stream carried; null on a page open, where nothing was counted. */
	fileCount: number | null,
	/**
	 * Which single file a per-file event was about, or null on the kinds that are not about one file.
	 * Absent on an api that predates the client-reported kinds, which is why it is nullable rather than
	 * required on the two kinds that always carry it.
	 */
	fileId: string | null,
}

/**
 * A transfer's download audit trail — `GET /transfer/{id}/download-events`, PREMIUM only.
 *
 * {@link events} is the most recent entries only; {@link total} is the exact number that exist. They differ
 * on a heavily downloaded transfer, and the view must say so rather than present a head as the whole trail.
 */
export interface TransferDownloadEvents {
	transferId: string,
	total: number,
	events: TransferDownloadEvent[],
}

export type _TransferRecipientDelivery =
	AssertNoSpecDrift<SpecDrift<TransferRecipientDelivery, Schemas["RecipientDeliveryView"]>>;
export type _TransferAnalytics =
	AssertNoSpecDrift<SpecDrift<TransferAnalytics, Schemas["TransferAnalyticsResponse"]>>;
export type _TransferDownloadEvent =
	AssertNoSpecDrift<SpecDrift<TransferDownloadEvent, Schemas["DownloadEventView"]>>;
export type _TransferDownloadEvents =
	AssertNoSpecDrift<SpecDrift<TransferDownloadEvents, Schemas["TransferDownloadEventsResponse"]>>;

/** The transfer's state as serialized on the wire — `expireAfter` is the Java enum NAME (Jackson default). */
type WireTransferState = Omit<TransferState, "expireAfter" | "senderCompany"> & {
	expireAfter: ExpiresAfterName | null,
	/** Absent on an api that predates kitecorp/transferify-ui#3186, which means the same as null: a person. */
	senderCompany?: SenderCompany | null,
};

/**
 * A list row as the REST route serializes it (`TransferSummaryDto`). Only {@link TransferApi.findListEntry} still
 * reads a list row over REST (batch 6e of kitecorp/transferify#418 moves it), so this and the mappers below serve it
 * alone.
 */
type WireTransferListEntry = WireTransferState & {
	fileCount: number,
	totalBytes: number,
	firstFileName?: string | null,
	/** The contact groups the transfer went to; optional in the spec, so absent reads as none. */
	recipientGroups?: string[],
	/** Encrypted without escrow (`Transfer.isZeroKnowledge()`); optional in the spec, so absent reads as escrow. */
	zeroKnowledge?: boolean,
};

/*
 * The row is bound through the page's own list (`["transfers"][number]`): a list binding compares whole elements by
 * plain assignability, without the leniency on optionality the field-by-field binding grants, and indexing through
 * the page still fails the build if the api renames the list.
 *
 * `senderCompany` is exempted because the generated type says less than the wire does, and the honest
 * hand-written `| null` stays: it is `@Nullable` in the api's `TransferStateDto`, and the spec says so
 * (`"type": "null"` beside the `$ref`), but openapi-typescript drops that null. Removable the day the
 * generated type carries it.
 */
type SpecUnderstatedStateNulls = "senderCompany";
export type _TransferListEntry = AssertNoSpecDrift<
	SpecDrift<WireTransferListEntry, Schemas["TransferSummaryPage"]["transfers"][number], SpecUnderstatedStateNulls>>;

/**
 * Maps one wire row of the paged list. Its size needs no conversion: the api sums each file as the sender
 * picked it, the number `transferFileOf` works out per file.
 */
function entryFromWire(dto: WireTransferListEntry): TransferListEntry {
	return {
		...stateFromWire(dto),
		fileCount: dto.fileCount,
		totalBytes: dto.totalBytes,
		firstFileName: dto.firstFileName ?? null,
		recipientGroups: dto.recipientGroups ?? [],
		zeroKnowledge: dto.zeroKnowledge === true,
	};
}

/** Maps the transfer's state onto the app-facing shape (enum NAME → numeric enum; missing lists → empty). */
function stateFromWire(dto: WireTransferState): TransferState {
	return {
		id: dto.id,
		...(dto.encodedId === undefined ? {} : {encodedId: dto.encodedId}),
		message: dto.message ?? null,
		keepOnline: dto.keepOnline,
		expireAfter: expiresAfterFromName(dto.expireAfter),
		// Defaulting to false keeps the flag total on the app side; the api serializes the primitive
		// boolean on every TransferDto (api#92), so the fallback only covers stale mocks.
		notifyOnDownload: dto.notifyOnDownload ?? false,
		createdDate: dto.createdDate,
		recipients: dto.recipients ?? [],
		expiresAt: dto.expiresAt ?? null,
		// The api serializes recoveryState on every TransferDto (#96, a required schema field), so the
		// fallback only covers stale mocks — and INELIGIBLE is the safe one: it offers nothing.
		recoveryState: dto.recoveryState ?? TransferRecoveryState.INELIGIBLE,
		recoverableUntil: dto.recoverableUntil ?? null,
		// Required on the wire (#220), so the fallback only covers stale mocks and cached payloads —
		// and UNSUPPORTED is the safe one: it offers no picker and no upsell.
		fileAdditionState: dto.fileAdditionState ?? TransferFileAdditionState.UNSUPPORTED,
		complete: dto.complete,
		// Required on the wire (#417), so the fallback only covers stale mocks and cached payloads —
		// and `false` is the safe one: it claims nothing, where a defaulted `true` would tell an owner
		// their working transfer is under review.
		suspended: dto.suspended ?? false,
		// Required on the wire (kitecorp/transferify#85), so the fallback only covers stale mocks and
		// cached payloads — and `false` is the safe one: it claims no hold, where a defaulted `true` would
		// hide the delete action on a transfer the server would happily delete.
		legalHold: dto.legalHold ?? false,
		// Required on the wire (kitecorp/transferify#85), so the fallback only covers stale mocks and
		// cached payloads — and UNSUPPORTED is the safe one: it offers nothing, where a defaulted
		// AVAILABLE would advertise an action the server may refuse.
		legalHoldState: dto.legalHoldState ?? TransferLegalHoldState.UNSUPPORTED,
		// Required on the wire (kitecorp/transferify-api-service#1192), so the fallback only covers an api
		// that predates the field, stale mocks and cached payloads; zero is the safe one: no notice.
		refusedDownloads: dto.refusedDownloads ?? 0,
		senderCompany: dto.senderCompany ?? null,
	};
}

const listClient = () => createClient(TransferListService, apiTransport);
const changeClient = () => createClient(TransferChangeService, apiTransport);

/** The transfer's state a change answers. The answer carries no files; one without the transfer is no answer. */
function changedStateOf(answer: {transfer?: TransferStateMessage}): TransferState {
	if (answer.transfer === undefined) {
		throw new RangeError("an answer without the transfer");
	}
	return transferStateOf(answer.transfer);
}

export class TransferApi {

	/**
	 * Removes ONE file by its file id — and with the transfer's last file, the transfer itself. Since
	 * kitecorp/transferify-api-service#220 the api additionally refuses removing one file from a SENT
	 * multi-file transfer without the `editAfterSending` capability (400 `error.transfer.editNotAllowed`);
	 * deleting the last remaining file stays allowed on every tier. `transferFileEdits` mirrors that split
	 * so the ui never offers a removal the api will refuse.
	 *
	 * "Sent" is the served `complete` field, which since kitecorp/transferify-api-service#381 covers an
	 * instant (P2P) transfer too — so this refusal now reaches instant transfers that used to bypass it.
	 */
	static async deleteFileBy(id: string): Promise<void> {
		await changeClient().deleteTransferFile({fileId: id});
	}

	/**
	 * Revokes an ENTIRE transfer by its RAW uuid (kitecorp/transferify-api-service#396): the api releases
	 * every file's stored bytes and deletes the transfer, so all recipient links die at once.
	 *
	 * A DISTINCT operation from {@link deleteFileBy}, not a loop over it. Taking a transfer down is a
	 * revoke rather than an edit, so it is UNGATED on every tier — including the FREE owner of a
	 * multi-file SENT transfer, for whom the per-file call refuses the first N-1 removals and therefore
	 * offered no way to withdraw the transfer at all. Never send this in place of a per-file removal:
	 * that one keeps its paid `editAfterSending` gate.
	 */
	static async deleteTransfer(id: string): Promise<void> {
		await changeClient().deleteTransfer({transferId: id});
	}

	/**
	 * Declares files to ADD to an already-sent transfer (kitecorp/transferify-api-service#220), returning
	 * each one's id. `id` is the RAW transfer uuid, exactly like {@link patchTransfer}; the
	 * ENCODED id needed by the multipart routes comes back in {@link AddFilesResponse.transferId}.
	 *
	 * The api is the authority on whether the transfer may be added to at all (capability, encryption
	 * flow, P2P, completeness, expiry) and answers a localized 400 otherwise — this only carries the
	 * request and surfaces that refusal.
	 */
	static async addFiles(id: string, request: AddFilesRequest): Promise<AddFilesResponse> {
		let data = await axios.post<any, AxiosResponse<AddFilesResponse>>(`/transfer/${id}/files`, request);
		return data.data;
	}

	/**
	 * One page of the owner's transfers (kitecorp/transferify-api-service#2163): at most the api's page size,
	 * cut from every transfer of the account in the given order, plus the account's totals. `offset` is the
	 * number of rows the caller already holds, so after a delete the next page starts at the row count it
	 * has now and skips nothing. Nothing in the answer is a signed link.
	 */
	static async getPage(sort: TransferListSort, offset: number): Promise<TransferListPage> {
		return transferListPageOf(await listClient().listTransfers({sort: ListTransfersRequest_Sort[sort], offset}));
	}

	/**
	 * One of the owner's transfers as its list row, by the ENCODED id an upload or instant registration
	 * handed out: the only id the success screen and the live peer-to-peer seeder hold. Null when the api
	 * answers 404, which means the transfer is gone or not the caller's; any other failure is thrown.
	 */
	static async findListEntry(encodedId: string): Promise<TransferListEntry | null> {
		try {
			// Encoded for the reason `DownloadApi.fileDownloadUrl` encodes a capability: a `/` or `?` in one
			// would point the request at another route.
			const {data} = await axios.get<WireTransferListEntry>(`${TRANSFER_UPLOADS_PATH}/${encodeURIComponent(encodedId)}`);
			return entryFromWire(data);
		} catch (error) {
			if (responseStatus(error) === 404) {
				return null;
			}
			throw error;
		}
	}

	/**
	 * Every file of one of the owner's transfers (RAW uuid), in the transfer's own order, as metadata only:
	 * what an expanded row lists. An owner's download asks for that one file's link when it is clicked.
	 */
	static async getFiles(transferId: string): Promise<TransferFile[]> {
		const files: TransferFile[] = [];
		// A page may hold fewer rows than its size when its rows are large, so each page starts at the number of files
		// held. The read ends at the total, and at a page that adds nothing (the transfer shrank while it read), so it
		// can never ask for the same page forever.
		for (;;) {
			const page = await listClient().listTransferFiles({transferId, offset: files.length});
			if (page.files.length === 0) {
				return files;
			}
			files.push(...page.files.map(transferFileOf));
			if (files.length >= page.totalFiles) {
				return files;
			}
		}
	}

	/**
	 * Post-send mutation (#93 extend expiry / #94 edit): calls `UpdateTransfer` and returns the updated
	 * transfer's state, with no files. The entitlement/window rules are the api's authority; this only carries the
	 * request and preserves the original rejection for presentation-layer classification.
	 */
	static async patchTransfer(id: string, body: PatchTransferBody): Promise<TransferState> {
		return changedStateOf(await changeClient().updateTransfer(updateTransferRequestOf(id, body)));
	}

	/**
	 * Spends an expired transfer's recovery (#96), re-opening its download link until the retained bytes
	 * are purged. A call of its own rather than a field of `UpdateTransfer`, because an update refuses expired
	 * transfers outright. It names only the transfer: the api derives the whole decision (entitlement, window,
	 * one-shot) from the transfer itself, and it is the authority; this only carries the request and surfaces the
	 * api's localized refusal.
	 */
	static async recoverTransfer(id: string): Promise<TransferState> {
		return changedStateOf(await changeClient().recoverTransfer({transferId: id}));
	}

	/**
	 * Places a legal hold (kitecorp/transferify#85): the link stops expiring and the transfer becomes
	 * undeletable until released. Body-less, like {@link recoverTransfer} — the api derives the whole
	 * decision (entitlement, shape, liveness, whether the storage can honour it) from the transfer, and
	 * it is the authority; this only carries the request and surfaces the localized refusal.
	 */
	static async placeLegalHold(id: string): Promise<TransferState> {
		return changedStateOf(await changeClient().placeLegalHold({transferId: id}));
	}

	/**
	 * Releases a legal hold, so the transfer resumes its normal expiry and becomes deletable again.
	 *
	 * Deliberately not gated on the plan here, because the api does not gate it either: a downgraded owner must
	 * still be able to stop pinning bytes they are billed for.
	 */
	static async releaseLegalHold(id: string): Promise<TransferState> {
		return changedStateOf(await changeClient().releaseLegalHold({transferId: id}));
	}

	/**
	 * Advanced analytics for one transfer the caller owns (kitecorp/transferify-api-service#99).
	 *
	 * `id` is the RAW transfer uuid, like {@link patchTransfer} and {@link recoverTransfer} — deliberately
	 * NOT the encoded share id that the anonymous `GET /transfers/{id}/stats` counter takes. Knowing the
	 * shareable id is what lets a recipient read that public counter; it must not also open the owner's
	 * analytics.
	 *
	 * The api is the authority on entitlement and ownership and answers a localized 400/404 — this only
	 * carries the request and surfaces that refusal.
	 */
	static async getAnalytics(id: string): Promise<TransferAnalytics> {
		const {data} = await axios.get<TransferAnalytics>(`/transfer/${id}/analytics`);
		// The api serializes the list on every response; the fallback only covers stale mocks and
		// keeps an absent list from becoming an "untracked" claim further down.
		return {...data, deliveries: data.deliveries ?? []};
	}

	/**
	 * The download audit trail for one transfer the caller owns (kitecorp/transferify#84).
	 *
	 * `id` is the RAW transfer uuid, exactly as {@link getAnalytics} takes it and for the same reason.
	 *
	 * A separate call from {@link getAnalytics} because the trail is unbounded where those metrics are a
	 * fixed-size summary: it is fetched only once the reader asks to see the evidence, so expanding a row
	 * never pays for a list nobody opened.
	 */
	static async getDownloadEvents(id: string): Promise<TransferDownloadEvents> {
		const {data} = await axios.get<TransferDownloadEvents>(`/transfer/${id}/download-events`);
		// The api serializes the list on every response; the fallback only covers stale mocks, and an
		// absent list must read as "no downloads", never as a shorter trail than the total claims.
		return {...data, events: data.events ?? []};
	}
}
